import { useState } from 'react';
import { api } from '../api.js';

/*
 * „Мейл“ на печатния изглед — общото за документ (PrintDocument) и
 * обединена фактура (PrintInvoice, 30.10.2026). Ако клиентът вече има
 * имейл, първо показва точното писмо (до кого, тема, текст, прикачен файл)
 * и праща чак с „Изпрати“; иначе пита за адрес и само го записва в
 * картона — второто натискане праща.
 *
 * Хук, а не компонент: бутонът стои в лентата, съобщението — под нея,
 * прозорците — отделно; страницата ги слага където трябва. Вика се преди
 * ранните return-и на страницата, затова стойностите може още да липсват.
 *
 *   endpoint    — POST адресът (`/api/documents/:id/send-mail` и т.н.)
 *   body        — още полета към всяка заявка (напр. { form })
 *   email, partnerId, partnerName — клиентът
 *   viewHref    — „виж“ до прикачения файл
 *   onEmailSaved(email) — след запис на нов имейл в картона
 */
export function useMailSend({ endpoint, body = {}, email, partnerId, partnerName, viewHref, onEmailSaved }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [emailInput, setEmailInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  async function sendNow() {
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      const res = await api.post(endpoint, preview
        ? {
          ...body, to: preview.to, subject: preview.subject, text: preview.text,
          files: (preview.files ?? []).map(({ name, type, data }) => ({ name, type, data })),
        }
        : body);
      setPreview(null);
      setNotice(`Изпратено на ${res.to}.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function onClick() {
    setNotice(null);
    setError(null);
    if (!email) { setEmailInput(''); setModalOpen(true); return; }
    setBusy(true);
    try {
      setPreview(await api.post(endpoint, { ...body, preview: true }));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function saveEmail() {
    const value = emailInput.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError('Невалиден имейл адрес');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/partners/${partnerId}`, { email: value });
      onEmailSaved?.(value);
      setModalOpen(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const button = (
    <button
      className={email ? '' : 'warn'}
      disabled={busy || !partnerId}
      onClick={onClick}
      title={!partnerId ? 'Документът няма клиент' : email ? `Прати на ${email}` : 'Клиентът няма въведен имейл'}
    >
      {busy ? 'Изпращане…' : 'Мейл'}
    </button>
  );

  const noticeRow = (notice || error) && (
    <div className="print-toolbar" style={{ color: error ? 'var(--danger)' : 'var(--good, #2a7)' }}>
      {error ?? notice}
      <button className="link" onClick={() => { setNotice(null); setError(null); }}>×</button>
    </div>
  );

  const modals = (
    <>
      {preview && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="card-head">Изпращане по имейл — проверете писмото</div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '4px 10px', margin: '8px 0' }}>
              <span className="muted">До</span>
              <span>
                <input value={preview.to} autoComplete="off" style={{ width: '100%' }}
                  onChange={(e) => setPreview((m) => ({ ...m, to: e.target.value }))} />
                <span className="hint" style={{ display: 'block' }}>
                  Няколко адреса — със запетая.{' '}
                  <button type="button" className="link" onClick={(e) => {
                    setPreview((m) => ({ ...m, to: m.to.trim() ? `${m.to.trim().replace(/[,;]\s*$/, '')}, ` : '' }));
                    const pole = e.currentTarget.closest('span').previousSibling;
                    setTimeout(() => { pole.focus(); pole.setSelectionRange(pole.value.length, pole.value.length); }, 0);
                  }}>+ още адрес</button>
                </span>
              </span>
              {preview.bcc && (<><span className="muted">Скрито копие</span><span>{preview.bcc}</span></>)}
              <span className="muted">Тема</span>
              <input value={preview.subject}
                onChange={(e) => setPreview((m) => ({ ...m, subject: e.target.value }))} />
              <span className="muted">Прикачен файл</span>
              <span>
                📎 {preview.attachment}{' '}
                <a href={viewHref} target="_blank" rel="noreferrer">виж</a>
                {(preview.files ?? []).map((f, i) => (
                  <div key={i}>
                    📎 {f.name} <span className="muted">({Math.round(f.size / 1024)} KB)</span>{' '}
                    <button type="button" className="link" onClick={() => setPreview((m) => ({
                      ...m, files: m.files.filter((_, j) => j !== i),
                    }))}>махни</button>
                  </div>
                ))}
                <div>
                  <label className="link" style={{ cursor: 'pointer' }}>
                    + добави файл
                    <input type="file" multiple style={{ display: 'none' }} onChange={async (e) => {
                      const izbrani = [...e.target.files];
                      e.target.value = '';
                      const procheteni = await Promise.all(izbrani.map((f) => new Promise((ok, ne) => {
                        const r = new FileReader();
                        r.onload = () => ok({
                          name: f.name, type: f.type || null, size: f.size,
                          data: String(r.result).split(',')[1] ?? '',
                        });
                        r.onerror = () => ne(r.error);
                        r.readAsDataURL(f);
                      })));
                      setPreview((m) => ({ ...m, files: [...(m.files ?? []), ...procheteni] }));
                    }} />
                  </label>
                </div>
              </span>
            </div>
            <textarea
              value={preview.text}
              rows={Math.max(8, preview.text.split('\n').length + 2)}
              onChange={(e) => setPreview((m) => ({ ...m, text: e.target.value }))}
              style={{ width: '100%', fontSize: 14, lineHeight: 1.45, padding: '10px 12px' }}
            />
            <div className="hint">Текстът и темата може да се поправят — клиентът получава написаното тук.</div>
            {error && <div className="error" style={{ marginTop: 6 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setPreview(null)} disabled={busy}>Откажи</button>
              <button className="primary" onClick={sendNow} disabled={busy}>
                {busy ? 'Изпращане…' : 'Изпрати'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="modal-backdrop" onClick={() => !busy && setModalOpen(false)}>
          <div className="modal" style={{ maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
            <div className="card-head">Имейл на клиента</div>
            <p className="muted" style={{ margin: '4px 0 10px' }}>
              {partnerName} няма записан имейл. Въведете го, за да се запише в картона му —
              документът се праща при следващо натискане на „Мейл".
            </p>
            <label className="field">
              Имейл
              <input
                type="email"
                autoComplete="off"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveEmail(); }}
                placeholder="office@klient.bg"
                autoFocus
              />
            </label>
            {error && <div className="error" style={{ marginTop: 6 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setModalOpen(false)} disabled={busy}>Отказ</button>
              <button className="primary" onClick={saveEmail} disabled={busy}>
                {busy ? 'Записване…' : 'Запис'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return { button, notice: noticeRow, modals };
}
